import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface RoleVisibility {
  consumer: boolean;
  creator: boolean;
  admin: boolean;
  superadmin: boolean; // always true — cannot lock out super admin
}

export interface PageVisibilityConfig {
  pageKey: string;
  label: string;
  visibleTo: RoleVisibility;
}

export interface SectionVisibilityConfig {
  pageKey: string;
  sectionKey: string;
  label: string;
  visibleTo: RoleVisibility;
  order: number;
}

export interface VisibilitySettings {
  pages: PageVisibilityConfig[];
  sections: SectionVisibilityConfig[];
  updatedAt?: any;
  updatedBy?: string;
}

/* ------------------------------------------------------------------ */
/*  Defaults                                                           */
/* ------------------------------------------------------------------ */

const allVisible: RoleVisibility = {
  consumer: true,
  creator: true,
  admin: true,
  superadmin: true,
};

const creatorsAndAbove: RoleVisibility = {
  consumer: false,
  creator: true,
  admin: true,
  superadmin: true,
};

const adminOnly: RoleVisibility = {
  consumer: false,
  creator: false,
  admin: true,
  superadmin: true,
};

export const DEFAULT_PAGES: PageVisibilityConfig[] = [
  { pageKey: 'home',            label: 'Home',              visibleTo: { ...allVisible } },
  { pageKey: 'profile',         label: 'Profile',           visibleTo: { ...allVisible } },
  { pageKey: 'feed',            label: 'Feed',              visibleTo: { ...allVisible } },
  // Gordon: "Marketplace should be default on, for visitors and all users."
  // (Was adminOnly, which silently hid it even when the Customize toggle was ON.)
  { pageKey: 'marketplace',     label: 'Marketplace',       visibleTo: { ...allVisible } },
  { pageKey: 'events',          label: 'Events',            visibleTo: { ...allVisible } },
  { pageKey: 'news',            label: 'News',              visibleTo: { ...allVisible } },
  { pageKey: 'wishlist',        label: 'Wishlist',          visibleTo: { ...allVisible } },
  { pageKey: 'party',           label: 'Listening Party',   visibleTo: { ...allVisible } },
  { pageKey: 'jam-session',     label: 'Jam Session',       visibleTo: { ...creatorsAndAbove } },
  { pageKey: 'radio',           label: 'Radio',             visibleTo: { ...allVisible } },
  { pageKey: 'synchronization', label: 'Sync Licensing',    visibleTo: { ...creatorsAndAbove } },
  { pageKey: 'stats',           label: 'Stats',             visibleTo: { ...allVisible } },
  { pageKey: 'music-coach',     label: 'Coach',             visibleTo: { ...allVisible } },
  { pageKey: 'media',           label: 'Media Vault',       visibleTo: { ...creatorsAndAbove } },
  { pageKey: 'artwork-studio',  label: 'Artwork Studio',    visibleTo: { ...creatorsAndAbove } },
  { pageKey: 'film-maker',      label: 'Film Maker',        visibleTo: { ...creatorsAndAbove } },
  { pageKey: 'wallet',          label: 'Wallet',            visibleTo: { ...allVisible } },
  { pageKey: 'earnings',        label: 'Earnings',          visibleTo: { ...creatorsAndAbove } },
  { pageKey: 'bookkeeping',     label: 'Bookkeeping',       visibleTo: { ...allVisible } },
  { pageKey: 'settings',        label: 'Settings',          visibleTo: { ...creatorsAndAbove } },
];

export const DEFAULT_SECTIONS: SectionVisibilityConfig[] = [
  // Home page sections (mirrors home-settings.ts defaults)
  { pageKey: 'home', sectionKey: 'new-releases',       label: 'Releases',               visibleTo: { ...allVisible }, order: 0 },
  { pageKey: 'home', sectionKey: 'videos',             label: 'Videos',                 visibleTo: { ...allVisible }, order: 1 },
  { pageKey: 'home', sectionKey: 'featured-artists',   label: 'Artists',                visibleTo: { ...allVisible }, order: 2 },
  { pageKey: 'home', sectionKey: 'top-charts',         label: 'Charts',                 visibleTo: { ...allVisible }, order: 3 },
  { pageKey: 'home', sectionKey: 'live-events',        label: 'Events',                 visibleTo: { ...allVisible }, order: 4 },
  { pageKey: 'home', sectionKey: 'news',               label: 'News',                   visibleTo: { ...allVisible }, order: 5 },
  { pageKey: 'home', sectionKey: 'demos',              label: 'Demos & Beats',          visibleTo: { ...allVisible }, order: 6 },
  { pageKey: 'home', sectionKey: 'featured-labels',    label: 'Labels',                 visibleTo: { ...allVisible }, order: 7 },
  { pageKey: 'home', sectionKey: 'popular-playlists',  label: 'Playlists',              visibleTo: { ...allVisible }, order: 8 },
  { pageKey: 'home', sectionKey: 'marketplace',        label: 'Marketplace Highlights', visibleTo: { ...adminOnly }, order: 9 },
  { pageKey: 'home', sectionKey: 'radio',              label: 'Radio',                  visibleTo: { ...allVisible }, order: 10 },
  { pageKey: 'home', sectionKey: 'community',          label: 'Community Picks',        visibleTo: { ...allVisible }, order: 11 },
  { pageKey: 'home', sectionKey: 'users',              label: 'People',                 visibleTo: { ...allVisible }, order: 12 },
  // Profile page sections
  { pageKey: 'profile', sectionKey: 'social-media',    label: 'Social Media',         visibleTo: { ...allVisible }, order: 0 },
  { pageKey: 'profile', sectionKey: 'to-do',           label: 'To-Do List',           visibleTo: { ...allVisible }, order: 1 },
  { pageKey: 'profile', sectionKey: 'marketplace',     label: 'Marketplace',          visibleTo: { ...allVisible }, order: 2 },
  { pageKey: 'profile', sectionKey: 'media-vault',     label: 'Media Vault',          visibleTo: { ...creatorsAndAbove }, order: 3 },
];

/* ------------------------------------------------------------------ */
/*  Firestore CRUD                                                     */
/* ------------------------------------------------------------------ */

const SETTINGS_COLLECTION = 'siteSettings';
const SETTINGS_DOC_ID = 'page-visibility';

export async function getVisibilitySettings(): Promise<VisibilitySettings> {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
      const data = snapshot.data() as VisibilitySettings;
      // Merge with defaults so newly-added pages/sections appear automatically
      const existingPageKeys = new Set(data.pages.map(p => p.pageKey));
      const mergedPages = [...data.pages];
      for (const def of DEFAULT_PAGES) {
        if (!existingPageKeys.has(def.pageKey)) {
          mergedPages.push({ ...def });
        }
      }

      const existingSectionKeys = new Set(
        data.sections.map(s => `${s.pageKey}::${s.sectionKey}`)
      );
      const mergedSections = [...data.sections];
      for (const def of DEFAULT_SECTIONS) {
        if (!existingSectionKeys.has(`${def.pageKey}::${def.sectionKey}`)) {
          mergedSections.push({ ...def, order: mergedSections.length });
        }
      }

      return {
        ...data,
        pages: mergedPages,
        sections: mergedSections.sort((a, b) => a.order - b.order),
      };
    }
  } catch (error) {
    console.error('Error fetching visibility settings:', error);
  }

  return { pages: [...DEFAULT_PAGES], sections: [...DEFAULT_SECTIONS] };
}

export async function saveVisibilitySettings(
  settings: VisibilitySettings,
  userId: string
): Promise<void> {
  const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
  // Ensure superadmin is always true before saving
  const pages = settings.pages.map(p => ({
    ...p,
    visibleTo: { ...p.visibleTo, superadmin: true },
  }));
  const sections = settings.sections.map((s, i) => ({
    ...s,
    order: i,
    visibleTo: { ...s.visibleTo, superadmin: true },
  }));

  await setDoc(docRef, {
    pages,
    sections,
    updatedAt: Timestamp.now(),
    updatedBy: userId,
  });
}
